import { useState, useEffect, useCallback } from 'react';
import { quotesAPI, contactsAPI, settingsAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import {
  FileCheck, Plus, Send, Trash2, Eye, Mail, UserPlus, Loader2,
  Download, FileText, ChevronDown, Check, Ban, Clock, ArrowRightLeft, RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';

const formatCurrency = (val) => new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(val || 0);

const STATUS_CONFIG = {
  draft:     { label: 'Entwurf',     className: 'bg-slate-500/10 text-slate-400 border-slate-500/20' },
  sent:      { label: 'Versendet',   className: 'bg-indigo-500/10 text-indigo-400 border-indigo-500/20' },
  accepted:  { label: 'Angenommen',  className: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20' },
  rejected:  { label: 'Abgelehnt',   className: 'bg-red-500/10 text-red-400 border-red-500/20' },
  expired:   { label: 'Abgelaufen',  className: 'bg-amber-500/10 text-amber-400 border-amber-500/20' },
  converted: { label: 'In Rechnung', className: 'bg-cyan-500/10 text-cyan-400 border-cyan-500/20' },
};

function QuoteStatusBadge({ status }) {
  const config = STATUS_CONFIG[status] || STATUS_CONFIG.draft;
  return (
    <Badge className={`text-xs ${config.className}`} data-testid={`quote-status-badge-${status}`}>
      {config.label}
    </Badge>
  );
}

export default function QuotesPage() {
  const [quotes, setQuotes] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [companySettings, setCompanySettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [showPreview, setShowPreview] = useState(null);
  const [showEmail, setShowEmail] = useState(null);
  const [showInlineContact, setShowInlineContact] = useState(false);
  const [inlineContact, setInlineContact] = useState({ name: '', email: '', company: '', phone: '', contact_type: 'customer' });
  const [savingContact, setSavingContact] = useState(false);
  const [downloadingId, setDownloadingId] = useState(null);
  const [convertingId, setConvertingId] = useState(null);
  const [statusUpdatingId, setStatusUpdatingId] = useState(null);
  const [emailAddr, setEmailAddr] = useState('');
  const [sending, setSending] = useState(false);
  const [form, setForm] = useState({
    contact_id: '', contact_name: '', quote_date: new Date().toISOString().split('T')[0],
    valid_until: '', items: [{ description: '', quantity: 1, unit_price: 0 }], tax_rate: 19, notes: '',
  });

  const loadData = useCallback(async () => {
    try {
      const [qRes, cRes, sRes] = await Promise.all([
        quotesAPI.list(),
        contactsAPI.list(),
        settingsAPI.get(),
      ]);
      setQuotes(qRes.data.quotes || []);
      setContacts(cRes.data.contacts || []);
      setCompanySettings(sRes.data);
      const s = sRes.data;
      if (s?.tax_type === 'kleinunternehmer') {
        setForm(prev => ({ ...prev, tax_rate: 0 }));
      }
      // Default validity: 30 days
      const validDate = new Date();
      validDate.setDate(validDate.getDate() + 30);
      setForm(prev => ({ ...prev, valid_until: prev.valid_until || validDate.toISOString().split('T')[0] }));
    } catch {
      toast.error('Fehler beim Laden');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const addItem = () => setForm({ ...form, items: [...form.items, { description: '', quantity: 1, unit_price: 0 }] });
  const removeItem = (i) => setForm({ ...form, items: form.items.filter((_, idx) => idx !== i) });
  const updateItem = (i, field, value) => {
    const items = [...form.items];
    items[i] = { ...items[i], [field]: field === 'description' ? value : Number(value) };
    setForm({ ...form, items });
  };

  const netTotal = form.items.reduce((s, it) => s + it.quantity * it.unit_price, 0);
  const taxAmount = netTotal * (form.tax_rate / 100);
  const grossTotal = netTotal + taxAmount;

  const selectContact = (contactId) => {
    const c = contacts.find(ct => ct.id === contactId);
    setForm({ ...form, contact_id: contactId, contact_name: c ? c.name : '' });
  };

  const handleInlineContactSave = async () => {
    if (!inlineContact.name.trim()) { toast.error('Bitte Namen eingeben'); return; }
    setSavingContact(true);
    try {
      const res = await contactsAPI.create(inlineContact);
      const newContact = res.data.contact;
      toast.success(`Kontakt "${newContact.name}" erstellt`);
      setContacts(prev => [newContact, ...prev]);
      setForm(prev => ({ ...prev, contact_id: newContact.id, contact_name: newContact.name }));
      setShowInlineContact(false);
      setInlineContact({ name: '', email: '', company: '', phone: '', contact_type: 'customer' });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Fehler beim Erstellen');
    } finally { setSavingContact(false); }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.contact_name) { toast.error('Bitte Kontakt auswählen'); return; }
    try {
      await quotesAPI.create({ ...form, status: 'draft' });
      toast.success('Angebot erstellt');
      setShowCreate(false);
      setForm({
        contact_id: '', contact_name: '', quote_date: new Date().toISOString().split('T')[0],
        valid_until: new Date(Date.now() + 30 * 86400000).toISOString().split('T')[0],
        items: [{ description: '', quantity: 1, unit_price: 0 }], tax_rate: 19, notes: '',
      });
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Fehler beim Erstellen');
    }
  };

  const handleDelete = async (id) => {
    try {
      await quotesAPI.remove(id);
      toast.success('Angebot gelöscht');
      loadData();
    } catch { toast.error('Fehler beim Löschen'); }
  };

  const handleStatusChange = async (id, status) => {
    setStatusUpdatingId(id);
    try {
      await quotesAPI.updateStatus(id, status);
      toast.success(`Status auf "${STATUS_CONFIG[status]?.label || status}" geändert`);
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Fehler beim Status-Update');
    } finally { setStatusUpdatingId(null); }
  };

  const handleDownloadPdf = async (id, number) => {
    setDownloadingId(id);
    try {
      const res = await quotesAPI.downloadPdf(id);
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      // Mobile-friendly: open in new tab instead of download trick
      const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
      if (isMobile) {
        window.open(url, '_blank');
      } else {
        const a = document.createElement('a');
        a.href = url;
        a.download = `${number}.pdf`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      }
      setTimeout(() => window.URL.revokeObjectURL(url), 5000);
    } catch {
      toast.error('PDF-Download fehlgeschlagen');
    } finally { setDownloadingId(null); }
  };

  const handleSendEmail = async () => {
    if (!emailAddr || !showEmail) return;
    setSending(true);
    try {
      await quotesAPI.sendEmail(showEmail.id, { recipient_email: emailAddr });
      toast.success(`Angebot per E-Mail an ${emailAddr} gesendet`);
      setShowEmail(null);
      setEmailAddr('');
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'E-Mail-Fehler');
    } finally { setSending(false); }
  };

  const handleConvertToInvoice = async (id) => {
    setConvertingId(id);
    try {
      const res = await quotesAPI.convertToInvoice(id);
      const inv = res.data.invoice;
      toast.success(`Rechnung ${inv.invoice_number} aus Angebot erstellt`);
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Fehler beim Konvertieren');
    } finally { setConvertingId(null); }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20" data-testid="quotes-loading">
        <Loader2 className="w-6 h-6 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="quotes-page">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-heading font-extrabold text-3xl sm:text-4xl tracking-tight mb-2">Angebote</h1>
          <p className="text-sm sm:text-base text-muted-foreground">{quotes.length} Angebote insgesamt</p>
        </div>
        <Button
          onClick={() => setShowCreate(true)}
          className="bg-primary text-primary-foreground hover:bg-primary/90 shadow-[0_0_15px_rgba(0,229,255,0.3)]"
          data-testid="create-quote-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          <span className="hidden sm:inline">Neues Angebot</span>
          <span className="sm:hidden">Neu</span>
        </Button>
      </div>

      {/* Quotes Table */}
      <Card className="bg-card border border-border/50" data-testid="quotes-table-card">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-border/50">
                  <TableHead className="text-xs">Nr.</TableHead>
                  <TableHead className="text-xs">Kunde</TableHead>
                  <TableHead className="text-xs hidden sm:table-cell">Datum</TableHead>
                  <TableHead className="text-xs hidden md:table-cell">Gültig bis</TableHead>
                  <TableHead className="text-xs text-right">Betrag</TableHead>
                  <TableHead className="text-xs">Status</TableHead>
                  <TableHead className="text-xs text-right">Aktionen</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {quotes.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-12 text-muted-foreground">
                      Noch keine Angebote erstellt
                    </TableCell>
                  </TableRow>
                ) : quotes.map((q) => (
                  <TableRow key={q.id} className="border-border/30 hover:bg-accent/30" data-testid={`quote-row-${q.id}`}>
                    <TableCell className="font-mono-data text-xs font-medium text-primary">{q.quote_number}</TableCell>
                    <TableCell className="text-sm">{q.contact_name}</TableCell>
                    <TableCell className="text-xs text-muted-foreground hidden sm:table-cell">{q.quote_date}</TableCell>
                    <TableCell className="text-xs text-muted-foreground hidden md:table-cell">{q.valid_until}</TableCell>
                    <TableCell className="text-right font-mono-data text-sm font-medium">{formatCurrency(q.gross_total)}</TableCell>
                    <TableCell><QuoteStatusBadge status={q.status} /></TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button variant="ghost" size="sm" onClick={() => setShowPreview(q)} className="h-7 w-7 p-0" data-testid={`quote-preview-btn-${q.id}`}>
                          <Eye className="w-3.5 h-3.5" />
                        </Button>
                        <Button
                          variant="ghost" size="sm"
                          onClick={() => handleDownloadPdf(q.id, q.quote_number)}
                          disabled={downloadingId === q.id}
                          className="h-7 w-7 p-0"
                          data-testid={`quote-pdf-btn-${q.id}`}
                        >
                          {downloadingId === q.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => {
                          setShowEmail(q);
                          // Pre-fill recipient with the customer email:
                          // 1) the address snapshot on the quote itself, or
                          // 2) live lookup on the contact record (Stammdaten)
                          //    for legacy quotes created before the snapshot fix.
                          const fromSnapshot = (q.contact_email || '').trim();
                          const fromMasterData = (contacts.find(c => c.id === q.contact_id)?.email || '').trim();
                          setEmailAddr(fromSnapshot || fromMasterData || '');
                        }} className="h-7 w-7 p-0" data-testid={`quote-email-btn-${q.id}`}>
                          <Mail className="w-3.5 h-3.5" />
                        </Button>
                        {q.status !== 'converted' && (
                          <Button
                            variant="ghost" size="sm"
                            onClick={() => handleConvertToInvoice(q.id)}
                            disabled={convertingId === q.id}
                            className="h-7 w-7 p-0 text-cyan-400 hover:text-cyan-300"
                            title="In Rechnung umwandeln"
                            data-testid={`quote-convert-btn-${q.id}`}
                          >
                            {convertingId === q.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ArrowRightLeft className="w-3.5 h-3.5" />}
                          </Button>
                        )}
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm" className="h-7 w-7 p-0" disabled={statusUpdatingId === q.id} data-testid={`quote-status-dropdown-${q.id}`}>
                              {statusUpdatingId === q.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ChevronDown className="w-3.5 h-3.5" />}
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            {Object.entries(STATUS_CONFIG).filter(([k]) => k !== q.status && k !== 'converted').map(([key, cfg]) => (
                              <DropdownMenuItem key={key} onClick={() => handleStatusChange(q.id, key)} data-testid={`quote-set-status-${key}`}>
                                {cfg.label}
                              </DropdownMenuItem>
                            ))}
                          </DropdownMenuContent>
                        </DropdownMenu>
                        <Button variant="ghost" size="sm" onClick={() => handleDelete(q.id)} className="h-7 w-7 p-0 text-destructive hover:text-destructive/80" data-testid={`quote-delete-btn-${q.id}`}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* ==================== CREATE DIALOG ==================== */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto w-[95vw] sm:w-full" data-testid="create-quote-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl flex items-center gap-2">
              <FileCheck className="w-5 h-5 text-primary" />
              Neues Angebot erstellen
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreate} className="space-y-5">
            {/* Contact selection */}
            <div className="space-y-2">
              <Label>Kunde</Label>
              <div className="flex gap-2">
                <Select value={form.contact_id} onValueChange={selectContact}>
                  <SelectTrigger className="bg-background flex-1" data-testid="quote-contact-select">
                    <SelectValue placeholder="Kontakt auswählen..." />
                  </SelectTrigger>
                  <SelectContent>
                    {contacts.map(c => (
                      <SelectItem key={c.id} value={c.id}>{c.name} {c.company ? `(${c.company})` : ''}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button type="button" variant="outline" size="sm" onClick={() => setShowInlineContact(!showInlineContact)} data-testid="quote-inline-contact-btn">
                  <UserPlus className="w-4 h-4" />
                </Button>
              </div>
            </div>

            {/* Inline contact creation */}
            {showInlineContact && (
              <Card className="bg-accent/30 border-primary/20">
                <CardContent className="pt-4 space-y-3">
                  <p className="text-sm font-medium text-primary">Neuen Kontakt anlegen</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <Input placeholder="Name *" value={inlineContact.name} onChange={e => setInlineContact({ ...inlineContact, name: e.target.value })} className="bg-background" data-testid="quote-inline-name" />
                    <Input placeholder="E-Mail" value={inlineContact.email} onChange={e => setInlineContact({ ...inlineContact, email: e.target.value })} className="bg-background" data-testid="quote-inline-email" />
                    <Input placeholder="Firma" value={inlineContact.company} onChange={e => setInlineContact({ ...inlineContact, company: e.target.value })} className="bg-background" />
                    <Input placeholder="Telefon" value={inlineContact.phone} onChange={e => setInlineContact({ ...inlineContact, phone: e.target.value })} className="bg-background" />
                  </div>
                  <Button type="button" size="sm" onClick={handleInlineContactSave} disabled={savingContact} data-testid="quote-inline-save-btn">
                    {savingContact ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Check className="w-4 h-4 mr-1" />}
                    Kontakt speichern
                  </Button>
                </CardContent>
              </Card>
            )}

            {/* Dates */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Angebotsdatum</Label>
                <Input type="date" value={form.quote_date} onChange={e => setForm({ ...form, quote_date: e.target.value })} className="bg-background" data-testid="quote-date-input" />
              </div>
              <div className="space-y-2">
                <Label>Gültig bis</Label>
                <Input type="date" value={form.valid_until} onChange={e => setForm({ ...form, valid_until: e.target.value })} className="bg-background" data-testid="quote-valid-until-input" />
              </div>
            </div>

            {/* Items */}
            <div className="space-y-3">
              <Label>Positionen</Label>
              {form.items.map((item, i) => (
                <div key={i} className="flex gap-2 items-start" data-testid={`quote-item-${i}`}>
                  <Input placeholder="Beschreibung" value={item.description} onChange={e => updateItem(i, 'description', e.target.value)} className="bg-background flex-1" data-testid={`quote-item-desc-${i}`} />
                  <Input type="number" min="1" value={item.quantity} onChange={e => updateItem(i, 'quantity', e.target.value)} className="bg-background w-16 sm:w-20" placeholder="Menge" data-testid={`quote-item-qty-${i}`} />
                  <Input type="number" step="0.01" min="0" value={item.unit_price} onChange={e => updateItem(i, 'unit_price', e.target.value)} className="bg-background w-24 sm:w-28" placeholder="Preis" data-testid={`quote-item-price-${i}`} />
                  {form.items.length > 1 && (
                    <Button type="button" variant="ghost" size="sm" onClick={() => removeItem(i)} className="text-destructive h-10 w-10 p-0 flex-shrink-0">
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              ))}
              <Button type="button" variant="outline" size="sm" onClick={addItem} data-testid="quote-add-item-btn">
                <Plus className="w-4 h-4 mr-1" /> Position hinzufügen
              </Button>
            </div>

            {/* Tax & Notes */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Steuersatz (%)</Label>
                <Input type="number" min="0" max="100" value={form.tax_rate} onChange={e => setForm({ ...form, tax_rate: Number(e.target.value) })} className="bg-background w-24" data-testid="quote-tax-rate-input" />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Anmerkungen</Label>
              <Textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} placeholder="Optionale Bemerkungen zum Angebot..." className="bg-background" data-testid="quote-notes-input" />
            </div>

            {/* Totals */}
            <div className="bg-accent/50 rounded-lg p-4 space-y-1 text-sm" data-testid="quote-totals">
              <div className="flex justify-between"><span className="text-muted-foreground">Netto:</span><span className="font-mono-data">{formatCurrency(netTotal)}</span></div>
              {form.tax_rate > 0 && (
                <div className="flex justify-between"><span className="text-muted-foreground">USt ({form.tax_rate}%):</span><span className="font-mono-data">{formatCurrency(taxAmount)}</span></div>
              )}
              <Separator className="my-2 bg-border/30" />
              <div className="flex justify-between font-bold text-primary"><span>Gesamt:</span><span className="font-mono-data">{formatCurrency(grossTotal)}</span></div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowCreate(false)}>Abbrechen</Button>
              <Button type="submit" className="bg-primary text-primary-foreground" data-testid="quote-submit-btn">
                <FileCheck className="w-4 h-4 mr-2" />
                Angebot erstellen
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* ==================== PREVIEW DIALOG ==================== */}
      {showPreview && (
        <Dialog open={!!showPreview} onOpenChange={() => setShowPreview(null)}>
          <DialogContent className="max-w-lg w-[95vw] sm:w-full" data-testid="quote-preview-dialog">
            <DialogHeader>
              <DialogTitle className="font-heading flex items-center gap-2">
                <Eye className="w-5 h-5 text-primary" />
                {showPreview.quote_number}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div><span className="text-muted-foreground">Kunde:</span><p className="font-medium">{showPreview.contact_name}</p></div>
                <div><span className="text-muted-foreground">Datum:</span><p className="font-medium">{showPreview.quote_date}</p></div>
                <div><span className="text-muted-foreground">Gültig bis:</span><p className="font-medium">{showPreview.valid_until}</p></div>
                <div><span className="text-muted-foreground">Status:</span><div className="mt-1"><QuoteStatusBadge status={showPreview.status} /></div></div>
              </div>
              <Separator />
              <div className="space-y-2">
                <p className="text-xs text-muted-foreground font-medium uppercase tracking-wider">Positionen</p>
                {(showPreview.items || []).map((it, i) => (
                  <div key={i} className="flex justify-between text-sm">
                    <span>{it.description} <span className="text-muted-foreground">x{it.quantity}</span></span>
                    <span className="font-mono-data">{formatCurrency(it.quantity * it.unit_price)}</span>
                  </div>
                ))}
              </div>
              <Separator />
              <div className="space-y-1 text-sm">
                <div className="flex justify-between"><span className="text-muted-foreground">Netto:</span><span className="font-mono-data">{formatCurrency(showPreview.net_total)}</span></div>
                {showPreview.tax_rate > 0 && (
                  <div className="flex justify-between"><span className="text-muted-foreground">USt ({showPreview.tax_rate}%):</span><span className="font-mono-data">{formatCurrency(showPreview.tax_amount)}</span></div>
                )}
                <div className="flex justify-between font-bold text-primary text-base"><span>Gesamt:</span><span className="font-mono-data">{formatCurrency(showPreview.gross_total)}</span></div>
              </div>
              {showPreview.notes && (
                <>
                  <Separator />
                  <p className="text-sm text-muted-foreground">{showPreview.notes}</p>
                </>
              )}
              {showPreview.from_quote_number && (
                <p className="text-xs text-cyan-400">Erstellt aus Angebot: {showPreview.from_quote_number}</p>
              )}
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* ==================== EMAIL DIALOG ==================== */}
      {showEmail && (
        <Dialog open={!!showEmail} onOpenChange={() => setShowEmail(null)}>
          <DialogContent className="max-w-md w-[95vw] sm:w-full" data-testid="quote-email-dialog">
            <DialogHeader>
              <DialogTitle className="font-heading flex items-center gap-2">
                <Mail className="w-5 h-5 text-primary" />
                Angebot per E-Mail senden
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Angebot <strong className="text-foreground">{showEmail.quote_number}</strong> an Kunde senden.
              </p>
              <div className="space-y-2">
                <Label>Empfänger E-Mail</Label>
                <Input
                  type="email"
                  value={emailAddr}
                  onChange={e => setEmailAddr(e.target.value)}
                  placeholder="kunde@firma.de"
                  className="bg-background"
                  data-testid="quote-email-input"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowEmail(null)}>Abbrechen</Button>
              <Button onClick={handleSendEmail} disabled={sending || !emailAddr} className="bg-primary text-primary-foreground" data-testid="quote-send-email-btn">
                {sending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                Senden
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
